import { useMemo } from "react";
import classes from "./BookingOverview.module.css"

import { useBooking } from "../../../store/bookingStore";
import { getWeekendSurchargeLineLabel } from "../../../util/getWeekendSurchargeLineLabel";
import { getDiscountLineLabel } from "../../../util/getDiscountLineLabel";
import { parseLocalDateString } from "../../../util/parseLocalDate";
import { calculatePreExtrasTotal } from "../../../util/calculatePreExtrasTotal";

export const BookingOverview = ({ setCurrentSlide, nextSlide, bookingStudioData }) => {
    const { bookingData } = useBooking();

    const isPackagePricing = useMemo(() => {
        // IMPORTANT:
        // - Only treat as package pricing when pricingMode is explicitly "PACKAGES"
        // - Studios with pricingMode === "HOURLY" must stay hourly even if packages exist
        return bookingStudioData?.pricingMode === 'PACKAGES';
    }, [bookingStudioData?.pricingMode]);

    const formatDate = (date) => {
        const parsed = parseLocalDateString(date);
        if (!parsed) return '';
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return parsed.toLocaleDateString('de-DE', options);
    }

    const calculateDuration = (startTime, endTime) => {
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        
        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;
        
        const durationInMinutes = endTotalMinutes - startTotalMinutes;
        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;
        
        return `${hours} Stunden${minutes > 0 ? ` und ${minutes} Minuten` : ''}`;
    };

    const calculatePrice = () => {
        // Derive duration from selected times (for display only)
        const [startHour, startMinute] = (bookingData?.startTime || '00:00').split(':').map(Number);
        const [endHour, endMinute] = (bookingData?.endTime || '00:00').split(':').map(Number);
        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;
        const durationInHours = (endTotalMinutes - startTotalMinutes) / 60;

        if (isPackagePricing) {
            const basePrice = bookingData?.totalStudioPrice || 0;
            return { basePrice, durationInHours };
        }

        // Hourly pricing: use values already calculated via calculateStudioPrice in Booking.jsx
        const basePrice = bookingData?.baseStudioPrice || 0;

        return { basePrice, durationInHours };
    };

    const { basePrice, durationInHours } = calculatePrice();
    const duration = calculateDuration(bookingData?.startTime, bookingData?.endTime);
    const syncedPackagePrice = Number(bookingData?.baseStudioPrice);
    const packageDisplayPrice = syncedPackagePrice > 0
        ? syncedPackagePrice
        : (Number(bookingData?.packageBasePrice) || 0)
            + (Number(bookingData?.packagePeoplePrice) || 0);

    // This step appears before Creative and Equipment selection. Build its total
    // from the rows shown here instead of subtracting later additions from
    // totalPrice, which may already contain equipment or a server campaign price.
    const bookingOverviewTotal = calculatePreExtrasTotal(bookingData);

    // No extra side-effects: Booking.jsx / BookingFirst already keep bookingData totals in sync


  return (
    <div className={classes.container}>
        <section className={classes.dateTime}>
            <h2 className="titleSemibold3">Datum & Uhrzeit</h2>
            <div className={classes.dateTimeDetails}>
                <div className={classes.dateTimeDetailsLeft}>
                    <p className="regularLarge">{formatDate(bookingData?.date)}</p>
                    <p className="regularLarge">{bookingData?.startTime} - {bookingData?.endTime}</p>
                </div>
                <button className={classes.edit} onClick={() => setCurrentSlide(0)}>Bearbeiten</button>
            </div>
            <div className={classes.durationContainer}>
                {/* <button className={classes.addDay}>Ein Tag mehr hinzufügen</button> */}
                <p className="regularXxs">Dauer: {duration}</p>
            </div>
        </section>
        {/* <div className={classes.horizontalLine}></div>
        <section className={classes.participants}>
            <h2 className="titleSemibold3">Teilnehmer</h2>
            <div className={classes.participantsDetails}>
                <p className="regularLarge">{bookingStudioData?.numberOfPeople}</p>
                <button className={classes.edit}>Bearbeiten</button>
            </div>
        </section> */}
        <div className={classes.horizontalLine}></div>
        <section className={classes.price}>
            <h2 className="titleSemibold3">Preis</h2>
            <div className={classes.priceDetails}>
                <p>
                    {isPackagePricing
                        ? "Paketpreis"
                        : `${durationInHours > 0 && basePrice
                              ? `${Math.round((basePrice / durationInHours) * 100) / 100} € x ${durationInHours} Stunden`
                              : `${bookingStudioData?.pricePerHour} € x ${durationInHours} Stunden`}`}
                </p>
                <p>
                    {isPackagePricing
                        // Prefer the synchronized base price; package fields cover the first render.
                        ? `${packageDisplayPrice} €`
                        : `${basePrice} €`}
                </p>
            </div>
            {/* Rabatt */}
            {bookingData?.appliedDiscount > 0 && (
                <div className={classes.priceDetails}>
                    <p>{getDiscountLineLabel(bookingData)}</p>
                    <p style={{whiteSpace: 'nowrap'}}>-{bookingData.appliedDiscount} €</p>
                </div>
            )}
            {/* Wochenendaufschlag */}
            {bookingData?.appliedSurcharge > 0 && (
                <div className={classes.priceDetails}>
                    <p>{getWeekendSurchargeLineLabel(bookingData)}</p>
                    <p style={{whiteSpace: 'nowrap'}}>+{bookingData.appliedSurcharge} €</p>
                </div>
            )}
            {/* Extra Gebühren */}
            {bookingData?.appliedFees && bookingData.appliedFees.length > 0 && (
                bookingData.appliedFees.map((fee) => (
                    <div className={classes.priceDetails} key={fee.id}>
                        <p>{fee.description || 'Gebühr'}</p>
                        <p>+{fee.amount} €</p>
                    </div>
                ))
            )}
        </section>
        <div className={classes.horizontalLine}></div>
        <section className={classes.total}>
            <h2 className="titleSemibold3">Gesamtbetrag</h2>
            <p className="titleSemibold3">{bookingOverviewTotal} €</p>
        </section>
        {/* <div className={classes.horizontalLine}></div> */}
        {/* <div className={classes.nextStepContainer}>
            <p className={`${classes.nextStep} regularXxs`}>
                Als Nächstes: Auf der nächsten Seite kannst du einen Fotografen für dein Projekt engagieren und zusätzliches Equipment buchen.
            </p>
        </div> */}
        <div className={classes.bookingTotal}>
            <button className={classes.weiterButton} onClick={nextSlide}>Weiter</button>
        </div>
    </div>
  );
};

export default BookingOverview;
